import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { MapPin } from "iconoir-react";
import { avisiumProjects } from "@/lib/projects";
import { SEO } from "@/components/layout/SEO";

export default function Projects() {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState("All");

  const categories = ["All", ...Array.from(new Set(avisiumProjects.map(p => p.category[language])))];

  const filtered = filter === "All" 
    ? avisiumProjects 
    : avisiumProjects.filter(p => p.category[language] === filter);

  return (
    <>
      <SEO 
        title={t("Nos Projets", "Our Projects")}
        description={t(
          "Découvrez l'impact d'AVISIUM à travers nos projets de planification de mobilité, d'ingénierie et d'intelligence territoriale au Maroc et à l'international.",
          "Explore AVISIUM's impact through our mobility planning, engineering, and territorial intelligence projects in Morocco and abroad."
        )}
      />
      <PageHeader 
        overlineFr="Notre Impact"
        overlineEn="Our Impact"
        titleFr="Intelligence Spatiale. Impact Délivré."
        titleEn="Spatial Intelligence. Impact Delivered."
        descriptionFr="Découvrez notre portefeuille de projets en planification de la mobilité, ingénierie des infrastructures et intelligence territoriale."
        descriptionEn="Explore our portfolio of projects across mobility planning, infrastructure engineering, and territorial intelligence."
      />

      <section className="py-6 bg-warm-white/95 backdrop-blur-md border-b border-mineral-dark sticky top-[72px] z-40 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
        <Container>
          <div className="flex items-center gap-3 overflow-x-auto pb-2 pt-1 px-1 -mx-1 snap-x select-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`snap-start whitespace-nowrap px-6 py-2.5 text-sm font-medium rounded-full transition-all duration-300 border ${
                  filter === cat 
                    ? "bg-charcoal text-white border-charcoal shadow-md scale-100" 
                    : "bg-white text-graphite border-mineral-dark hover:border-amber hover:text-charcoal hover:shadow-sm scale-95 origin-center hover:scale-100"
                }`}
              >
                {cat === "All" ? t("Tous", "All") : cat}
              </button>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-20 lg:py-24 bg-warm-white min-h-[50vh]">
        <Container>
          <motion.div layout className="space-y-24">
            <AnimatePresence mode="popLayout">
              {filtered.map((project, index) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center"
                >
                  <div className={`relative h-72 sm:h-80 lg:h-[480px] rounded-2xl bg-mineral overflow-hidden shadow-sm ${index % 2 !== 0 ? 'lg:order-last' : ''}`}>
                    <img
                      src={project.image}
                      alt={language === 'fr' ? project.title.fr : project.title.en}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105 mix-blend-multiply opacity-95"
                    />
                    <div className="absolute top-5 left-5 z-10">
                      <span className="text-xs font-semibold px-4 py-1.5 rounded-full bg-charcoal text-white shadow-md">
                        {project.category[language]}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-3xl md:text-4xl font-display font-semibold text-charcoal leading-tight mb-3">
                        {language === 'fr' ? project.title.fr : project.title.en}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-graphite-light font-medium">
                        <MapPin width={16} height={16} />
                        {project.location[language]}
                      </div>
                    </div>

                    <div className="space-y-5 pt-2">
                      <div className="bg-white/50 p-5 rounded-xl border border-mineral-dark shadow-sm">
                        <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                          {t("Le Défi", "The Challenge")}
                        </h4>
                        <p className="text-sm text-graphite leading-relaxed">
                          {language === 'fr' ? project.challenge.fr : project.challenge.en}
                        </p>
                      </div>

                      <div className="bg-white/50 p-5 rounded-xl border border-mineral-dark shadow-sm">
                        <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                          {t("Notre Approche", "Our Approach")}
                        </h4>
                        <p className="text-sm text-graphite leading-relaxed">
                          {language === 'fr' ? project.approach.fr : project.approach.en}
                        </p>
                      </div>

                      <div className="bg-charcoal p-5 rounded-xl text-white shadow-md">
                        <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                          {t("L'Impact", "The Impact")}
                        </h4>
                        <p className="text-sm text-warm-white/90 font-medium leading-relaxed">
                          {language === 'fr' ? project.impact.fr : project.impact.en}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </Container>
      </section>
    </>
  );
}
